import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { authApi } from '@/api/endpoints/auth'
import AppFooter from '@/components/common/AppFooter'
import { useUI } from '@/stores/ui'

function PasswordResetPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const addToast = useUI((state) => state.addToast)

  const token = searchParams.get('token')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{
    email?: string
    password?: string
    confirmPassword?: string
  }>({})

  // If token exists, show reset form, otherwise show request form
  const isResetMode = !!token

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setLoading(true)

    if (!email) {
      setErrors({ email: 'Email is required' })
      setLoading(false)
      return
    }

    try {
      await authApi.resetPassword({ email })
      addToast({
        message: 'If an account exists with this email, a password reset link has been sent.',
        type: 'success',
      })
      navigate('/login')
    } catch (error: any) {
      addToast({
        message: 'An error occurred. Please try again.',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setLoading(true)

    // Validation
    if (!password || password.length < 8) {
      setErrors({ password: 'Password must be at least 8 characters' })
      setLoading(false)
      return
    }

    if (password !== confirmPassword) {
      setErrors({ confirmPassword: 'Passwords do not match' })
      setLoading(false)
      return
    }

    if (!token) {
      addToast({ message: 'Missing reset token. Please use the link from your email.', type: 'error' })
      setLoading(false)
      return
    }

    try {
      await authApi.confirmPasswordReset({ token, new_password: password })
      addToast({
        message: 'Password reset successfully. Please log in with your new password.',
        type: 'success',
      })
      navigate('/login')
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to reset password. The link may have expired.',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 page-transition">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2 font-display">Catechism Cloud</h1>
          <p className="text-gray-600">
            {isResetMode ? 'Reset your password' : 'Request password reset'}
          </p>
        </div>

        {isResetMode ? (
          <form onSubmit={handleResetPassword} className="card space-y-6 hover-raise">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`input-field ${errors.password ? 'border-red-500' : ''}`}
                placeholder="Enter new password (min 8 characters)"
                required
                minLength={8}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`input-field ${errors.confirmPassword ? 'border-red-500' : ''}`}
                placeholder="Confirm new password"
                required
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Resetting password...' : 'Reset Password'}
            </button>

            <div className="text-center">
              <Link to="/login" className="text-sm text-gold-600 hover:text-gold-700">
                Back to login
              </Link>
            </div>
          </form>
        ) : (
          <form onSubmit={handleRequestReset} className="card space-y-6 hover-raise">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                placeholder="your.email@example.com"
                required
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending reset link...' : 'Send Reset Link'}
            </button>

            <p className="text-sm text-gray-600 text-center mt-4">
              If you are a parent who has already registered, please be patient while your parish administator activates your account. Your login credentials (username and password) will not work until your registration has been approved!
            </p>

            <div className="text-center">
              <Link to="/login" className="text-sm text-gold-600 hover:text-gold-700">
                Back to login
              </Link>
            </div>
          </form>
        )}
      </div>
      <AppFooter />
    </div>
  )
}

export default PasswordResetPage
